<template>
  <!-- Invoice lines table -->
  <table class="gk-table gk-table-invoicelines gk-table--responsive gk-table--striped gk-input-light">

    <!-- Table head -->
    <thead>
      <td>{{ $gettext('Quantity') }}</td>
      <td>{{ $gettext('Description') }}</td>
      <td>
        <b-dropdown toggle-class="gk-vat-input--toggle">
          <template #button-content>
            <span v-if="invoice.line_amount_is_gross">{{ $gettext('Unit price (Inc. VAT)') }}</span>
            <span v-else>{{ $gettext('Unit price (Ex. VAT)') }}</span>
          </template>
          <b-dropdown-item @click="invoice.line_amount_is_gross = true">{{ $gettext("Inc. VAT") }}</b-dropdown-item>
          <b-dropdown-item @click="invoice.line_amount_is_gross = false">{{ $gettext("Ex. VAT") }}</b-dropdown-item>
        </b-dropdown>
      </td>
      <td>
        <span v-if="invoice.line_amount_is_gross">{{ $gettext('Total (Inc. VAT)') }}</span>
        <span v-else>{{ $gettext('Total (Ex. VAT)') }}</span>
      </td>
      <td>{{ $gettext('Tax Rate') }}</td>
      <td></td>
    </thead>

    <!-- Table body lines table -->
    <tbody>
      <tr v-for="(invoice_line, invoice_index) in invoice_lines" :key="'invoice_line-' + invoice_index">
        <!-- Quantity -->
        <td class="col-quantity">
          <div class="d-flex">
            <b-form-input v-model.number="invoice_lines[invoice_index].multiplier" type="number" required @input="computeNetValue(invoice_index)" :disabled="isCorrection" min="0" step="any" />
            <div class="quantity-times"><font-awesome-icon icon="fa-solid fa-xmark" /></div>
          </div>
        </td>

        <!-- Description -->
        <td class="col-desc"><b-form-input v-model="invoice_lines[invoice_index].description" type="text" /></td>

        <!-- Unit price -->
        <td class="col-price">
          <b-input-group class="icon-contained-input-group">
            <b-input-group-prepend>
              <b-input-group-text>€</b-input-group-text>
            </b-input-group-prepend>
            <b-form-input 
              v-model.number="invoice_lines[invoice_index].amount"
              type="number" required placeholder="0.00" step="any"
              class="text-right"
              @input="computeNetValue(invoice_index)" min="0"
             />
          </b-input-group>
        </td>

        <!-- Sub/Total (Ex. VAT) -->
        <td class="text-right"><span>{{ invoice_line.subtotal_amount | formatNumber }}</span></td>

        <!-- Tax Rate -->
        <td class="col-vat">
            <VatInput 
              :value="invoice_lines[invoice_index].tax_rate"
              v-if="!invoice.vat_reversed_charge"
              :disabled="invoice_line.product"
              @input="(newTaxRate) => { setNewTaxRate(newTaxRate, invoice_index) }"
            />
            <div v-else class="vat-reversed">
              <font-awesome-icon icon="fa-solid fa-minus" />
            </div>
        </td>

        <!-- Actions (remove invoice line) -->
        <td>
          <GekkoButton 
            @click="removeInvoiceLine(invoice_index)"
            icon="fa-solid fa-trash-can"
            class="text-danger"
            variant="link"
          />
        </td>
      </tr>
      <tr v-if="showError"><td colspan="6"><div class="d-block invalid-feedback" >{{ $gettext('This field is required.') }}</div></td></tr>
    </tbody>

    <!-- Table footer Totals and vat lines -->
    <tfoot>

      <!-- Subtotal -->
      <tr>
        <td></td>
        <td></td>
        <td class="text-right">{{ $gettext('Subtotal') }}</td>
        <td class="text-right">{{ subtotal | formatNumber }}</td>
      </tr>

      <!-- Vat lines -->
      <template v-for="(vat_amount, vat_index) in vatTotals" >
        <tr v-if="!invoice.vat_reversed_charge && vat_index > 0" :key="'vat_totals-' + vat_index">
          <td></td>
          <td></td>
          <td class="text-right">{{ vat_index }}% <span>{{ $gettext('VAT') }}</span></td>
          <td class="text-right">{{ vat_amount | formatNumber }}</td>
        </tr>
      </template>
      
      <tr v-if="invoice.vat_reversed_charge">
        <td></td>
        <td></td>
        <td class="text-right">{{ $gettext('VAT') }}</td>
        <td class="text-right"> <font-awesome-icon icon="fa-solid fa-minus" /></td>
        <td colspan="2" style="font-size: 14px; line-height: 1.2" >{{ $gettext('Reversed charge') }}</td>
      </tr>

      <!-- TODO Spanish retention IRPF -->
      <!-- <tr v-if="currentCompany.country === 'ES'">
        <td class="text-right">
        {{ $gettext('Retention') }}
        <div class="dropdown small-dropdown">
          <div data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">

            <input name="retention_tax" ng-model="corrCtrl.invoice.retention_tax_percentage"
                      ng-change="corrCtrl.setRetention(corrCtrl.invoice.retention_tax_percentage)"
                      class="" placeholder="15.0%" size="4" type="number" step="any" ng-required="true">

          </div>
          <ul class="dropdown-menu" role="menu" aria-labelledby="dLabel">
            <li v-for="(a_retention_rate, rindex) in RETENTION_RATES" :key="'ret_rate-' + rindex">
              <a ng-click="corrCtrl.setRetention(a_retention_rate)">{{ a_retention_rate }}%</a>
            </li>
          </ul>
        </div>
        %
        </td>
        <td>{{ invoice.retention_tax_amount | formatNumber }}</td>
      </tr> -->

      <!-- Total -->
      <tr>
        <td></td>
        <td></td>
        <td class="text-right">{{ $gettext('Total') }}</td>
        <td class="text-right">{{ total | formatNumber }}</td>
      </tr>

      <!-- VAT Reversed checkbox -->
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td colspan="2" style="white-space: nowrap;">
          <b-form-checkbox class="checkbox-bg-color" v-model="invoice.vat_reversed_charge" switch>
            <span>{{ $gettext('VAT Reverse Charge') }}</span>
          </b-form-checkbox>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import { mapGetters } from "vuex";
import VatInput from "@/components/invoices/ui/VatInput";
import GekkoButton from "@/components/GekkoButton";

export default {
  name: 'InvoiceLines',
  components: {
    VatInput,
    GekkoButton
  },
  props: {
    value: {
      type: Array,
      required: true
    },
    invoice: {
      type: Object,
      required: true
    },
    showError: {
      type: Boolean,
      default: false
    },
    isCorrection: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      lineWatch: null,
      default_invoice_line: {
        order: 0,
        multiplier: 1,
        amount: 0,
        subtotal_amount: 0,
        tax_rate: null,
        description: ''
      }
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany"
    }),
    invoice_lines: {
      get () { return this.value },
      set (invoice_lines) { this.$emit('input', invoice_lines)  }
    },
    subtotal () {
      let subTotal = 0
      this.invoice_lines.forEach(invoice_line => {
        if (this.invoice.line_amount_is_gross) {
          if (invoice_line.vat_exempt || this.invoice.vat_reversed_charge) {
            subTotal += invoice_line.multiplier * invoice_line.amount
          }else{
            subTotal += invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
          }
        } else {
          subTotal += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        }
      })
      return subTotal
    },
    vatTotals () {
      const vat_totals = {}
      this.invoice_lines.forEach(invoice_line => {
        if((invoice_line.amount > 0 || invoice_line.amount < 0) && !invoice_line.vat_exempt) {
          if (!vat_totals[invoice_line.tax_rate]) {
            vat_totals[invoice_line.tax_rate] = 0
          }
          if (this.invoice.line_amount_is_gross) {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100 - Math.round((invoice_line.multiplier * invoice_line.amount / ( 1 + invoice_line.tax_rate / 100)) * 100) / 100
          } else {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * (invoice_line.tax_rate / 100) * 100) / 100
          }
        }
      })
      return vat_totals
    },
    total () {
      let total = 0
      if (this.invoice.line_amount_is_gross) {
        this.invoice_lines.forEach(invoice_line => {
          total += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        })
      } else {
        const subtotal = Math.round(parseFloat(this.subtotal) * 100) / 100
        let total_vat = 0

        for (let vat in this.vatTotals) {
          total_vat += Math.round(this.vatTotals[vat] * 100) / 100
        }
        
        if (this.invoice.vat_reversed_charge) total_vat = 0

        total = subtotal + total_vat
      }
      this.calculateRetention()
      if (this.invoice.retention_tax_amount) total += this.invoice.retention_tax_amount

      return total
    },
    watchableLine () {
      return this.invoice_lines[this.invoice_lines.length - 1]
    }
  },
  created () {
    this.setDefaults()
    this.addNewLine()
    this.calculateInvoiceLines()
  },
  methods: {
    /**
     * Adds a new line using a shallow default_invoice_line object and attach a watcher to it
     */
    addNewLine () {
      this.invoice_lines.push({ ...this.default_invoice_line })
      // unwatch the previous invoice_line and add a new one
      this.removeLineWatch()
      this.attachLineWatch()
    },
    /**
     * Removes one invoice_line from this.invoice_lines array
     * 
     * @param {number} index invoice_line index to remove
     */
    removeInvoiceLine (index) {
      if (this.invoice_lines.length > 1) {
        this.removeLineWatch()
        this.invoice_lines.splice(index, 1)
        this.attachLineWatch()
      }
    },
    /**
     * Calls destroy/delete function from a previously created $watcher
     */
    removeLineWatch () {
      if (this.lineWatch) this.lineWatch()
    },
    /**
     * Adds a new $watch() to a invoice_line from this.invoice_lines array
     * 
     * @param {number} line_idx invoice_line index to watch
     */
    attachLineWatch () {
      this.lineWatch = this.$watch(() => this.watchableLine, this.addNewLine, { deep: true })
    },
    /**
     * Sets currentCompany default settings to the default_invoice_line object
     */
    setDefaults () {
      // TODO add from angular invoice.abstract-create.controller.js 
      // parentInit() default_invoice_line.tax_rate
      // customer vs. senderInfo

      this.default_invoice_line.tax_rate = this.currentCompany.default_tax_rate
      this.default_invoice_line.vat_exempt = this.currentCompany.default_tax_rate_exempt
      // vm.object.vat_reversed_charge = vm.senderInfo['default_tax_rate_reversed'];
    },
    /**
     * Sets a new 'tax_rate' value to given invoice_line via VatInput component @input
     * 
     * @param {number,string} newTaxRate tax_rate value
     * @param {number} invoice_index invoice_line index
     */
    setNewTaxRate (newTaxRate, invoice_index) {
      this.invoice_lines[invoice_index].tax_rate = newTaxRate
      this.computeNetValue(invoice_index)
    },
    /**
     * Calculates subtotal_amount for the given invoice_line based on multiplier and amount
     * 
     * @param {number} invoice_index index from invoice_lines array
     */
    computeNetValue (invoice_index) {
      let invoice_line = this.invoice_lines[invoice_index]

      if(!this.invoice.vat_reversed_charge){
        if (invoice_line.vat_exempt || invoice_line.tax_rate === 'exempt') {
          invoice_line.tax_rate = 0
        }
        // invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      } else {
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      }
      // invoice_line = Math.round(invoice_line * 100) / 100; //make sure we have only 2 decimals
    },
    /**
     * Recalculates all invoice_lines subtotal_amounts and net values when selecting 'VAT Reverse Charge' checkbox
     */
    computeReversedCharge () {
      if (this.invoice.vat_reversed_charge) {
        for (let i=0; i < this.invoice_lines.length; i++) {
          this.invoice_lines[i].subtotal_amount = this.invoice_lines[i].amount * (1 - 0.21);
          this.computeNetValue(i);
        }
      } else {
        for (let i=0; i < this.invoice_lines.length; i++) {
          this.invoice_lines[i].subtotal_amount = this.invoice_lines[i].amount / (1 + this.invoice_lines[i].tax_rate / 100);
          this.computeNetValue(i);
        }
      }
    },
    /**
     * Calculates NetValue for all items on this.invoice_lines
     */
    calculateInvoiceLines () {
      for (var i = 0; i < this.invoice_lines.length; i++) {
        this.computeNetValue(i)
      }
    },
    /**
     * Sets invoice retention_tax_amount and retention_tax_percentage for spanish companies
     * 
     * @param {number} retention_percentage retention amount
     */
    calculateRetention (retention_percentage) {
      if (this.currentCompany.country !== 'ES') {
        this.invoice.retention_tax_amount = null
        this.invoice.retention_tax_percentage = null
        return
      }
      if (retention_percentage) {
        this.invoice.retention_tax_percentage = retention_percentage
      }

      this.invoice.retention_tax_amount = -Math.round(this.subtotal * this.invoice.retention_tax_percentage) / 100

      if (!this.invoice.retention_tax_amount) {
        this.invoice.retention_tax_amount = 0
      }
    }
  }
}
</script>

<style lang="scss">
  .gk-table-invoicelines {
    
    .col-quantity { 
      min-width: 100px; width: 120px; 
      input { text-align: center; }
      padding-right: 0;
      .quantity-times {
        padding-left: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }
    }
    .col-desc { min-width: 150px; width: 100%; }
    .col-price { min-width: 100px; width: 150px; }
    .col-vat { 
      min-width: 120px; 
      width: 130px; 
      text-align: center;
      .vat-reversed { color: $colorUnassigned; }
    }

    .form-control { padding: 0.25rem 0.5rem; }
    input[type=number]::-webkit-inner-spin-button, 
    input[type=number]::-webkit-outer-spin-button { 
      -webkit-appearance: none; 
      margin: 0; 
    }
    .input-group-prepend {
      .input-group-text {
        padding: 0.25rem 0.5rem;
        padding-right: 0;
        color: $colorTextLight;
      }
    }
    .input-group-prepend + input { border-left: 0; }


    thead {
      display: table-header-group !important;
      td {
        font-size: 14px;
        button { font-size: 14px; }
        white-space: nowrap;
        color: $colorTextBase;
        background: #E1E5E4;
        &:first-child { border-top-left-radius: $radiusMd; }
        &:last-child { border-top-right-radius: $radiusMd; }
      }
    }
    tbody {
      tr { cursor: initial !important; }
    }
    tfoot td, thead td {
      position: relative;
      border: none;
      vertical-align: middle;
      padding: 0.75rem;
    }
    tfoot {
      tr {
        td { padding: 0.5rem 0.75rem; }
        &:first-child { 
          border-top: 1px solid $colorBorderDash;
          td { padding-top: 2rem; } 
        }
        &:last-child { 
          td { padding-bottom: 2rem; }
        }
      }
    }
  }
</style>
